import { auth0ApiClient } from './auth0ApiClient'
import { GroupMessage, SendGroupMessageRequest, GetGroupMessagesParams } from '@/types/group'

class GroupMessageService {
  private readonly baseUrl = '/groups'

  /**
   * Get paginated message history for a group room, newest-first.
   */
  async getMessages(roomId: string, params?: GetGroupMessagesParams): Promise<GroupMessage[]> {
    const query = new URLSearchParams()
    if (params?.limit != null) query.set('limit', String(params.limit))
    if (params?.skip != null) query.set('skip', String(params.skip))
    const qs = query.toString()
    return auth0ApiClient.get<GroupMessage[]>(
      `${this.baseUrl}/${roomId}/messages${qs ? `?${qs}` : ''}`
    )
  }

  /**
   * Send a message to a group room, with optional file/image attachments.
   */
  async sendMessage(roomId: string, data: SendGroupMessageRequest): Promise<GroupMessage> {
    if (!data.attachments || data.attachments.length === 0) {
      return auth0ApiClient.post<GroupMessage>(`${this.baseUrl}/${roomId}/messages`, {
        content: data.content
      })
    }

    const formData = new FormData()
    formData.append('content', data.content)
    data.attachments.forEach((file) => formData.append('attachments', file))

    return auth0ApiClient.upload<GroupMessage>(`${this.baseUrl}/${roomId}/messages`, formData)
  }

  /**
   * Mark a group room as read for the current user, resetting their unread count.
   */
  async markAsRead(roomId: string): Promise<{ roomId: string; unreadCount: number }> {
    return auth0ApiClient.put<{ roomId: string; unreadCount: number }>(
      `${this.baseUrl}/${roomId}/read`
    )
  }

  /**
   * Hard-delete a message from a group room (app-admin only).
   */
  async deleteMessage(roomId: string, messageId: string): Promise<{ roomId: string; messageId: string }> {
    return auth0ApiClient.delete<{ roomId: string; messageId: string }>(
      `${this.baseUrl}/${roomId}/messages/${messageId}`
    )
  }
}

export const groupMessageService = new GroupMessageService()
